import { DataPalette } from "data-palette";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

type DataTheme = "light" | "dark";

type State = {
  dataTheme?: DataTheme;
  switchDataTheme: (mode: DataTheme) => void;
  dataPalette?: DataPalette;
  switchDataPalette: (mode: DataPalette) => void;
};
type ThemeProviderProps = {
  children: React.ReactNode;
  dataTheme?: DataTheme;
  dataPalette?: DataPalette;
};

const ThemeContext = createContext<State | undefined>(undefined);

function ThemeProvider({
  children,
  dataTheme: dataThemeProp,
  dataPalette: dataPaletteProp = "water",
}: ThemeProviderProps) {
  const [dataTheme, setDataTheme] = useState<DataTheme>();
  const [dataPalette, setDataPalette] = useState<DataPalette>();

  useEffect(() => {
    if (dataPaletteProp) {
      switchDataPalette(dataPaletteProp);
    }
  }, [dataPaletteProp]);

  useEffect(() => {
    if (dataThemeProp) {
      switchDataTheme(dataThemeProp);
    }
  }, [dataThemeProp]);

  useEffect(() => {
    if (window.matchMedia) {
      if (localStorage.getItem("data-theme")) {
        document.documentElement.setAttribute(
          "data-theme",
          localStorage.getItem("data-theme") as string
        );
        setDataTheme(localStorage.getItem("data-theme") as DataTheme);
      } else if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        document.documentElement.setAttribute("data-theme", "dark");
        setDataTheme("dark");
      }
    }
  }, []);

  const switchDataTheme = (mode: DataTheme) => {
    document.documentElement.setAttribute("data-theme", mode);
    setDataTheme(mode);
    localStorage.setItem("data-theme", mode);
  };

  const switchDataPalette = (mode: DataPalette) => {
    document.documentElement.setAttribute("data-palette", mode);
    setDataPalette(mode);
    localStorage.setItem("palette-theme", mode);
  };

  return (
    <ThemeContext.Provider
      value={{ dataTheme, switchDataTheme, dataPalette, switchDataPalette }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

function useTheme() {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
}

export { ThemeContext, ThemeProvider, useTheme };
